<template>
  <v-container id="create-user-form" fluid>
    <v-card outlined>
      <v-card-title class="justify-center" v-if="!update">{{ $t('user.createNewUser') }}</v-card-title>
      <v-card-title class="justify-center" v-else>{{ $t('user.editUser') }}</v-card-title>
      <v-container>
        <validation-observer ref="form">
          <v-form @submit.prevent="onSubmit">
            <v-tabs v-model="selectedTab" grow>
              <v-tab>{{ $t('user.basicInformations') }}</v-tab>
              <v-tab v-if="isAdmin">{{ $t('user.roles') }}</v-tab>
              <v-tab v-if="isAdmin">{{ $t('user.stores') }}</v-tab>
              <v-tab-item eager class="mb-6">
                <validation-provider rules="required" :name="$t('user.firstName')" v-slot="{ errors }">
                  <v-text-field
                    v-model="firstName"
                    :error-messages="errors"
                    :label="$t('user.firstName')"
                    required
                    clearable
                  ></v-text-field>
                </validation-provider>
                <validation-provider rules="required" :name="$t('user.lastName')" v-slot="{ errors }">
                  <v-text-field
                    v-model="lastName"
                    :error-messages="errors"
                    :label="$t('user.lastName')"
                    required
                    clearable
                  ></v-text-field>
                </validation-provider>
                <validation-provider :rules="email ? '' : 'required'" :name="$t('user.username')" v-slot="{ errors }">
                  <v-text-field
                    v-model="username"
                    :error-messages="errors"
                    :label="$t('user.username')"
                    required
                    clearable
                    :disabled="imposedUsername != undefined"
                  ></v-text-field>
                </validation-provider>
                <validation-provider
                  :rules="username ? 'email' : 'required|email'"
                  :name="$t('user.email')"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="email"
                    :error-messages="errors"
                    :label="$t('user.email')"
                    required
                    clearable
                    :disabled="imposedEmail != undefined"
                  ></v-text-field>
                </validation-provider>
                <v-switch v-if="!update && email" v-model="setPasswordByEmail" :label="$t('user.sendEmailSetup')" />
                <span v-if="!setPasswordByEmail || update || !email">
                  <validation-provider
                    :rules="update ? 'min:8' : 'required|min:8'"
                    v-slot="{ errors }"
                    :name="$t('user.newPassword')"
                    vid="newPassword"
                  >
                    <v-text-field
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      :label="$t('user.newPassword')"
                      :hint="$t('user.passwordHint')"
                      :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off'"
                      @click:append="showPassword = !showPassword"
                      counter
                      :error-messages="errors"
                      clearable
                    ></v-text-field>
                  </validation-provider>
                  <validation-provider
                    :rules="update ? 'confirmed:newPassword' : 'required|confirmed:newPassword'"
                    v-slot="{ errors }"
                    :name="$t('user.confirmPassword')"
                    vid="confirmPassword"
                  >
                    <v-text-field
                      v-model="confirmPassword"
                      :type="showPassword ? 'text' : 'password'"
                      :label="$t('user.confirmPassword')"
                      :hint="$t('user.passwordHint')"
                      counter
                      :error-messages="errors"
                      clearable
                    ></v-text-field>
                  </validation-provider>
                  <v-checkbox
                    v-if="update"
                    v-model="active"
                    :label="active ? $t('user.active') : $t('user.disabled')"
                    @change="showDialog = true"
                  >
                    <template slot="label"></template>
                  </v-checkbox>

                  <confirmation-disabled-reactive-user
                    v-model="showDialog"
                    :active="!active"
                    @validate="disabledOrReactiveUser()"
                    @close="closeConfirmationRefundDialog"
                  ></confirmation-disabled-reactive-user>
                </span>
              </v-tab-item>
              <v-tab-item eager>
                <validation-provider v-if="isAdmin" rules="required" :name="$t('user.role')" v-slot="{ errors }">
                  <v-select
                    v-model="role"
                    :items="roleOptions"
                    :error-messages="errors"
                    :label="$t('user.role')"
                    required
                  ></v-select>
                </validation-provider>
                <validation-provider
                  v-if="isAdmin && partnerSettings.unified_stock_access"
                  rules="required"
                  :name="$t('user.roleUnifiedStock')"
                  v-slot="{ errors }"
                >
                  <v-select
                    v-model="roleUnifiedStock"
                    :items="roleOptionsUnifiedStock"
                    :error-messages="errors"
                    :label="$t('user.roleUnifiedStock')"
                    required
                  ></v-select>
                </validation-provider>
                <validation-provider
                  v-if="isAdmin && partnerSettings.reception_access"
                  rules="required"
                  :name="$t('user.roleOctiStock')"
                  v-slot="{ errors }"
                >
                  <v-select
                    v-model="roleOctiStock"
                    :items="roleOptionsOctiStock"
                    :error-messages="errors"
                    :label="$t('user.roleOctiStock')"
                    required
                  ></v-select>
                </validation-provider>
                <validation-provider
                  v-if="isAdmin && partnerSettings.analytics_access"
                  rules="required"
                  :name="$t('user.roleAnalytics')"
                  v-slot="{ errors }"
                >
                  <v-select
                    v-model="roleAnalytics"
                    :items="roleOptionsAnalytics"
                    :error-messages="errors"
                    :label="$t('user.roleAnalytics')"
                    required
                  ></v-select>
                </validation-provider>
                <validation-provider
                  v-if="
                    isAdmin &&
                    partnerSettings.digital_in_store_access &&
                    partnerSettings.digital_in_store_access.enabled
                  "
                  rules="required"
                  :name="$t('user.roleDigitalInStore')"
                  v-slot="{ errors }"
                >
                  <v-select
                    v-model="roleDigitalInStore"
                    :items="roleOptionsDigitalInStore"
                    :error-messages="errors"
                    :label="$t('user.roleDigitalInStore')"
                    required
                  ></v-select>
                </validation-provider>
              </v-tab-item>
              <v-tab-item eager>
                <validation-provider
                  :name="$t('user.stores')"
                  v-if="isAdmin"
                  :rules="['oms-admin', 'octipas-admin'].includes(role) ? '' : 'required'"
                  v-slot="{ errors }"
                >
                  <v-autocomplete
                    v-model="otherLocations"
                    :items="storeList"
                    :loading="loadingStores"
                    :label="$t('user.stores')"
                    :error-messages="errors"
                    :item-text="(item) => `${item.location_id} - ${item.name}`"
                    item-value="location_id"
                    required
                    multiple
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip small v-if="index === 0">
                        <span v-if="index === 0">{{ `${item.location_id} - ${item.name}` }}</span>
                      </v-chip>
                      <span v-if="index === 1" class="grey--text caption">
                        {{ `(+${otherLocations.length - 1} ${$t('components.select.others')})` }}
                      </span>
                    </template>
                  </v-autocomplete>
                </validation-provider>
                <validation-provider
                  :name="$t('user.mainLocation')"
                  v-if="isAdmin"
                  v-slot="{ errors }"
                  :rules="['oms-admin', 'octipas-admin'].includes(role) ? '' : 'required'"
                >
                  <v-select
                    v-model="mainLocation"
                    :item-text="(item) => `${item.location_id} - ${item.name}`"
                    :items="userStoresList"
                    :label="$t('user.mainLocation')"
                    item-value="location_id"
                    :error-messages="errors"
                    :clearable="['oms-admin', 'octipas-admin'].includes(role)"
                  />
                </validation-provider>
              </v-tab-item>
            </v-tabs>
            <v-card-actions class="justify-center">
              <v-btn
                :disabled="loadingUsers"
                :loading="loadingUsers"
                class="ma-2"
                color="primary"
                outlined
                type="submit"
              >
                {{ $t('user.save') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </validation-observer>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
  import { mapActions, mapState } from 'vuex'

  import { ValidationObserver, ValidationProvider } from 'vee-validate'

  import { EventBus } from '../../event-bus'

  import ConfirmationDisabledReactiveUser from '@/components/dialogs/ConfirmationDisabledReactiveUser.vue'

  export default {
    name: 'userForm',
    components: {
      'validation-provider': ValidationProvider,
      'validation-observer': ValidationObserver,
      'confirmation-disabled-reactive-user': ConfirmationDisabledReactiveUser,
    },
    data() {
      return {
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        role: '',
        hasRegistration: false,
        roleUnifiedStock: '',
        hasRegistrationUnifiedStock: false,
        roleOctiStock: '',
        hasRegistrationOctiStock: false,
        roleAnalytics: '',
        hasRegistrationAnalytics: false,
        roleDigitalInStore: '',
        hasRegistrationDigitalInStore: false,
        password: '',
        confirmPassword: '',
        loadingStores: false,
        roleOptions: [
          { text: this.$t('users.admin'), value: 'oms-admin' },
          { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.seller'), value: 'default-user' },
        ],
        roleOptionsUnifiedStock: [
          { text: this.$t('users.admin'), value: 'admin' },
          { text: this.$t('users.globalStockManager'), value: 'global-stock-manager' },
          { text: this.$t('users.globalStockViewer'), value: 'global-stock-viewer' },
          { text: this.$t('users.storeStockManager'), value: 'store-stock-manager' },
          { text: this.$t('users.storeStockViewer'), value: 'store-stock-viewer' },
        ],
        roleOptionsOctiStock: [
          { text: this.$t('users.admin'), value: 'general-admin' },
          { text: this.$t('users.generalStockManager'), value: 'general-store-manager' },
          { text: this.$t('users.defaultManager'), value: 'default-manager' },
          { text: this.$t('users.defaultSeller'), value: 'default-seller' },
        ],
        roleOptionsAnalytics: [
          { text: this.$t('users.admin'), value: 'admin' },
          { text: this.$t('users.shopManager'), value: 'manager' },
          { text: this.$t('users.seller'), value: 'seller' },
        ],
        roleOptionsDigitalInStore: [
          { text: this.$t('users.admin'), value: 'admin' },
          { text: this.$t('users.areaManager'), value: 'areaManager' },
          { text: this.$t('users.storeManager'), value: 'storeManager' },
          { text: this.$t('users.sellerManager'), value: 'sellerManager' },
          { text: this.$t('users.seller'), value: 'seller' },
        ],
        storeList: [],
        showPassword: false,
        setPasswordByEmail: false,
        active: '',
        showDialog: false,
        selectedTab: null,
        mainLocation: null,
        otherLocations: [],
        isAdminUser: false,
        loadingUsers: false,
        userData: undefined,
      }
    },
    props: {
      imposedEmail: {
        type: String,
        default: undefined,
      },
      imposedUsername: {
        type: String,
        default: undefined,
      },
      update: {
        type: Boolean,
        default: false,
      },
      idUser: {
        type: String,
        default: undefined,
      },
      user: {
        type: Object,
        default: undefined,
      },
    },
    async mounted() {
      this.storeList = await this.getStores()
      if (this.user != undefined) {
        this.dispatchUserToForm(this.user)
      } else if (this.idUser) {
        this.getUser()
      } else {
        this.email = this.imposedEmail
        this.username = this.imposedUsername
      }
      if (this.isOctipasAdmin) {
        this.roleOptions.unshift({ text: this.$t('users.octipasAdmin'), value: 'octipas-admin' })
        this.roleOptionsAnalytics.unshift({ text: this.$t('users.octipasAdmin'), value: 'octipas-admin' })
      }
      if (this.$store.state.currentTab) {
        this.selectedTab = this.$store.state.currentTab
      }
    },
    computed: {
      ...mapState(['currentGroup', 'fusionAuthToken', 'isAdmin', 'partnerSettings', 'isOctipasAdmin', 'breadcrumbs']),
      userStoresList() {
        return this.storeList.filter((location) => this.otherLocations.includes(location.location_id))
      },
    },
    watch: {
      email: function () {
        if (!this.email) {
          this.setPasswordByEmail = false
        }
      },
      selectedTab: function (newVal) {
        this.$store.commit('setCurrentTab', newVal)
      },
      otherLocations(newLocations) {
        if (this.mainLocation && !newLocations.some((location) => location === this.mainLocation)) {
          this.mainLocation = null
        }
      },
    },
    methods: {
      ...mapActions(['updateBreadcrumb']),
      onSubmit: function () {
        this.$refs.form.validate().then((success) => {
          if (success) {
            this.setEmptyStringsToNull()
            if (this.$route.params.id) {
              this.updateUser()
            } else {
              this.createUser()
            }
          } else {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('user.incorrectMandatoryField'),
              color: 'error',
            })
          }
        })
      },
      async getStores() {
        this.loadingStores = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            itemsPerPage: false,
            fields: ['location_id', 'name', 'address'],
          },
        }
        try {
          let response = await this.$Amplify.API.get('locations', '/v1/int/locations', myInit)
          this.loadingStores = false
          return response.data
        } catch (e) {
          console.log(e)
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('orderWorkflow.locationsLoadingErrorMessage'),
            color: 'error',
          })
          this.loadingStores = false
        }
      },
      getUser() {
        this.loadingUsers = true
        if (!this.isAdmin && this.$route.params.id != this.fusionAuthToken.userId) {
          this.$router.push({ path: `/users/${this.fusionAuthToken.userId}` })
        }
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.get('users', '/user/' + this.$route.params.id, myInit)
          .then((res) => {
            this.loadingUsers = false
            this.dispatchUserToForm(res.user)
            let breadcrumbs = this.breadcrumbs.findIndex((item) => item.to === this.$route.path)
            if (breadcrumbs !== -1) {
              let name
              if (this.firstName && this.lastName) {
                name = `${this.firstName} ${this.lastName}`
              }

              this.updateBreadcrumb({
                index: breadcrumbs,
                value: {
                  text: name || this.username || this.email,
                },
              })
            }
          })
          .catch(() => {
            this.loadingUsers = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('user.anErrorOccurred'),
              color: 'error',
            })
            this.$router.push({ path: `/users` })
          })
      },
      updateUser() {
        this.loadingUsers = true
        let user = {
          registration: {
            applicationId: process.env.VUE_APP_SSO_APP_ID,
            roles: [this.role],
          },
          user: {
            id: this.$route.params.id,
            firstName: this.firstName,
            lastName: this.lastName,
            username: this.username,
            email: this.email,
            memberships: [
              {
                groupId: this.currentGroup.id,
              },
            ],
            data: {
              ...this.userData,
              other_locations: this.otherLocations.length ? this.otherLocations : null,
              main_location: this.mainLocation,
              userStore: this.mainLocation,
            },
          },
        }
        if (this.password) {
          user.user.password = this.password
        }
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: user,
        }
        this.updateRegistrations(this.$route.params.id)
        this.$Amplify.API.put('users', '/user/' + this.$route.params.id, myInit)
          .then(async (res) => {
            this.loadingUsers = false
            if (this.fusionAuthToken.userId == res.user.id) {
              this.$store.commit('setCurrentStore', this.mainLocation)
              this.$store.commit('setUserStores', this.otherLocations)
              let currentStore = this.storeList.find((location) => location.location_id === this.mainLocation)
              this.$store.commit('setCurrentStoreDetails', currentStore)
              EventBus.$emit('reLoadCounter')
            }
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('user.userUpdated'),
              color: 'success',
            })
          })
          .catch((e) => {
            this.loadingUsers = false
            let error = e.response.data
            if (error.fieldErrors) {
              for (const field in error.fieldErrors) {
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: error.fieldErrors[field][0].message,
                  color: 'error',
                })
              }
            } else if (error.generalErrors) {
              for (const index in error.generalErrors) {
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: error.generalErrors[index].message,
                  color: 'error',
                })
              }
            }
          })
      },
      async createUser() {
        this.loadingUsers = true
        let user = {
          registration: {
            applicationId: process.env.VUE_APP_SSO_APP_ID,
            roles: [this.role],
          },
          user: {
            firstName: this.firstName,
            lastName: this.lastName,
            username: this.username,
            email: this.email,
            memberships: [
              {
                groupId: this.currentGroup.id,
              },
            ],
            data: {
              other_locations: this.otherLocations.length ? this.otherLocations : null,
              main_location: this.mainLocation,
              userStore: this.mainLocation,
            },
          },
        }
        if (!this.setPasswordByEmail || !this.email) {
          user.sendSetPasswordEmail = false
          user.user.password = this.password
        } else {
          user.sendSetPasswordEmail = true
        }
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: user,
        }
        this.$Amplify.API.post('users', '/user/registration', myInit)
          .then(async (res) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('user.userCreated'),
              color: 'success',
            })
            if (res.user?.id) {
              this.hasRegistration = true
              await this.updateRegistrations(res.user.id, true)
            }
            this.loadingUsers = false
            await this.$router.push('/users')
          })
          .catch((e) => {
            this.loadingUsers = false
            let error = e.response.data
            if (error.fieldErrors) {
              for (const field in error.fieldErrors) {
                //alert(error.fieldErrors[field][0].message)
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: error.fieldErrors[field][0].message,
                  color: 'error',
                })
              }
            } else if (error.generalErrors) {
              for (const index in error.generalErrors) {
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: error.generalErrors[index].message,
                  color: 'error',
                })
              }
            }
          })
      },
      dispatchUserToForm(user) {
        this.email = user.email
        this.firstName = user.firstName
        this.lastName = user.lastName
        this.username = user.username
        this.active = user.active
        for (let registration of user.registrations) {
          if (registration.applicationId === process.env.VUE_APP_SSO_APP_ID) {
            this.hasRegistration = true
            this.role = registration.roles?.[0]
          } else if (registration.applicationId === process.env.VUE_APP_UNIFIED_STOCK_SSO_APP_ID) {
            this.hasRegistrationUnifiedStock = true
            this.roleUnifiedStock = registration.roles?.[0]
          } else if (registration.applicationId === process.env.VUE_APP_OCTI_STOCK_SSO_APP_ID) {
            this.hasRegistrationOctiStock = true
            this.roleOctiStock = registration.roles?.[0]
          } else if (registration.applicationId === process.env.VUE_APP_ANALYTICS_SSO_APP_ID) {
            this.hasRegistrationAnalytics = true
            this.roleAnalytics = registration.roles?.[0]
          } else if (registration.applicationId === process.env.VUE_APP_DIGITAL_IN_STORE_SSO_APP_ID) {
            this.hasRegistrationDigitalInStore = true
            this.roleDigitalInStore = registration.roles?.[0]
          }
        }

        if (user.data) {
          this.userData = user.data
          this.otherLocations = user.data.other_locations || []
          this.mainLocation = user.data.main_location || user.data.userStore
          if (!this.otherLocations.length && this.mainLocation) {
            let store = this.storeList.find((item) => item.location_id === this.mainLocation)
            if (store) {
              this.otherLocations.push(store.location_id)
            }
          }
        }
      },
      setEmptyStringsToNull() {
        if (typeof this.username == 'string' && this.username.trim() == '') {
          this.username = null
        }
        if (typeof this.email == 'string' && this.email.trim() == '') {
          this.email = null
        }
      },
      disabledOrReactiveUser() {
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        if (this.active) {
          myInit.queryStringParameters = {
            reactivate: true,
          }
          this.$Amplify.API.put('users', '/user/' + this.$route.params.id, myInit)
            .then(() => {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('user.successReactive'),
                color: 'success',
              })
              this.$router.push({ name: 'users' })
            })
            .catch(() => {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('user.anErrorOccurred'),
                color: 'error',
              })
            })
        } else {
          this.$Amplify.API.del('users', '/user/' + this.$route.params.id, myInit)
            .then(() => {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('user.successDisable'),
                color: 'success',
              })
              this.$router.push({ name: 'users' })
            })
            .catch(() => {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('user.anErrorOccurred'),
                color: 'error',
              })
            })
        }
      },
      async updateRegistrations(userId, isCreateUser = false) {
        // update OMS role
        if (this.role && !isCreateUser) {
          if (this.hasRegistration) {
            await this.updateRegistration(process.env.VUE_APP_SSO_APP_ID, this.role, userId)
          } else {
            await this.addRegistration(process.env.VUE_APP_SSO_APP_ID, this.role, userId)
            this.hasRegistration = true
          }
        }
        // Update unified stock role
        if (this.roleUnifiedStock) {
          if (this.hasRegistrationUnifiedStock) {
            await this.updateRegistration(process.env.VUE_APP_UNIFIED_STOCK_SSO_APP_ID, this.roleUnifiedStock, userId)
          } else {
            await this.addRegistration(process.env.VUE_APP_UNIFIED_STOCK_SSO_APP_ID, this.roleUnifiedStock, userId)
            this.hasRegistrationUnifiedStock = true
          }
        }
        // Update OctiStock role
        if (this.roleOctiStock) {
          if (this.hasRegistrationOctiStock) {
            await this.updateRegistration(process.env.VUE_APP_OCTI_STOCK_SSO_APP_ID, this.roleOctiStock, userId)
          } else {
            await this.addRegistration(process.env.VUE_APP_OCTI_STOCK_SSO_APP_ID, this.roleOctiStock, userId)
            this.hasRegistrationOctiStock = true
          }
        }
        // Update analytic Role
        if (this.roleAnalytics) {
          if (this.hasRegistrationAnalytics) {
            await this.updateRegistration(process.env.VUE_APP_ANALYTICS_SSO_APP_ID, this.roleAnalytics, userId)
          } else {
            await this.addRegistration(process.env.VUE_APP_ANALYTICS_SSO_APP_ID, this.roleAnalytics, userId)
            this.hasRegistrationAnalytics = true
          }
        }
        // Update digital in store Role
        if (this.roleDigitalInStore) {
          if (this.hasRegistrationDigitalInStore) {
            await this.updateRegistration(
              process.env.VUE_APP_DIGITAL_IN_STORE_SSO_APP_ID,
              this.roleDigitalInStore,
              userId
            )
          } else {
            await this.addRegistration(process.env.VUE_APP_DIGITAL_IN_STORE_SSO_APP_ID, this.roleDigitalInStore, userId)
            this.hasRegistrationDigitalInStore = true
          }
        }
      },
      async updateRegistration(applicationId, role, userId) {
        let registration = {
          registration: {
            applicationId: applicationId,
            roles: [role],
          },
        }
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: registration,
        }
        await this.$Amplify.API.put('users', '/user/registration/' + userId, myInit)
      },
      async addRegistration(applicationId, role, userId) {
        let registration = {
          registration: {
            applicationId: applicationId,
            roles: [role],
          },
        }
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: registration,
        }
        await this.$Amplify.API.post('users', '/user/registration/' + (this.$route.params.id || userId), myInit)
      },
      closeConfirmationRefundDialog() {
        this.active = !this.active
        this.showDialog = false
      },
    },
  }
</script>
<style lang="css" scoped>
  .v-input.v-text-field ::v-deep .v-label.v-label--active {
    line-height: 21px;
  }
</style>
